<template>
  <div ref="tableContainer" class="table-container" :class="!isTableWidthMoreThanScreen ? 'fixed' : ''" :style="{ height, width }">
    <table>
      <thead>
        <tr>
          <template v-if="hasSelectRow">
            <th class="checkbox">
              <Checkbox :acceptIndeterminate="isIndeterminateTotal && !isCheckedTotal && isRowSelected" :indeterminate="isIndeterminateTotal" :checked="isCheckedTotal" @change="onChangeCheckBoxTotal" />
            </th>
          </template>
          <template v-for="(colTable, indexCol) of columns">
            <template v-if="!!colTable.sorter">
              <th 
                :key="indexCol" 
                class="table-header sort"
                :style="{
                  width: colTable.width + 'px',
                  maxWidth: colTable.width + 'px',
                  minWidth: colTable.width + 'px',
                }"
                @click="(event) => onClickHeader(event, colTable.sorter, colTable.dataIndex)"
              >
                <div class="title">
                  <div>
                    <template v-if="colTable && colTable.slotTitle && hasSlot(colTable.slotTitle)">
                      <slot :name="colTable.slotTitle" :col="colTable" />
                    </template>
                    <template v-else>
                      {{ colTable.title }}
                    </template>
                  </div>
                  <div class="sort-icon" :class="{ current: sortField === colTable.dataIndex, increase: sortIncrease, decrease: !sortIncrease }">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </th>
            </template>
            <template v-else>
              <th
                :key="indexCol"
                :style="{
                  width: colTable.width + 'px',
                  maxWidth: colTable.width + 'px',
                  minWidth: colTable.width + 'px'
                }"
                class="table-header"
              >
                <template v-if="colTable && colTable.slotTitle && hasSlot(colTable.slotTitle)">
                  <slot :name="colTable.slotTitle" :col="colTable" />
                </template>
                <template v-else>
                  {{ colTable.title }}
                </template>
              </th>
            </template>
          </template>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!paginedData.length">
          <td :colspan="columns.length">
            <div class="text-center">There is no record in table</div>
          </td>
        </tr>
        <tr 
          v-for="(record, indexRecord) of paginedData" 
          :key="record.id || indexRecord" 
          :class="{ clickable: rowClickable }" 
          @click="onRowClick(record, indexRecord)"
        >
          <template v-if="hasSelectRow">
            <td class="checkbox" @click="rowClickable && $event.stopPropagation()">
              <Checkbox :name="nameGroupCheckbox" :acceptIndeterminate="false" :indeterminate="false" :checked="record.isChecked" @change="(event) => onChangeSingleCheckBox(record, indexRecord, event)" />
            </td>
          </template>
          <td
            v-for="(colTable, indexCol) of columns"
            :key="indexCol"
            :style="{
              width: colTable.width + 'px',
              maxWidth: colTable.width + 'px',
              minWidth: colTable.width + 'px'
            }"
          >
            <template v-if="colTable && colTable.slot && hasSlot(colTable.slot)">
              <slot :name="colTable.slot" :record="record" />
            </template>
            <template v-else>
              {{ record[colTable.dataIndex] }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  const outputChangeRowName = 'changeSelectedRow';

  import { genRandomId } from '@shared/helpers/utils';
  export default {
    name: 'PaginationTable',
    props: {
      clientPaging: {
        type: Boolean,
        default: () => true
      },
      pagination: {
        type: Object,
        default: () => ({})
      },
      hasSelectRow: {
        type: Boolean,
        default: () => false
      },
      rowSelection: {
        type: Array,
        default: () => []
      },
      columns: {
        type: Array,
        default: () => []
      },
      dataSource: {
        type: Array,
        default: () => []
      },
      tableSize: {
        type: Object,
        default: () => ({
          height: '100%',
          width: '100%'
        })
      },
      rowClickable: {
        type: Boolean,
        default: () => false
      }
    },
    watch: {
      tableSize: {
        immediate: true,
        handler({ height, width }) {
          this.setSizeTable(width, height);
        }
      },
      pagination: {
        immediate: false,
        handler() {
          this.paginDataTable();
        }
      },
      dataSource: {
        immediate: false,
        handler() {
          this.paginDataTable();
        }
      }
    },
    data() {
      return {
        height: '100%',
        width: '100%',
        totalColWidth: 0,
        isTableWidthMoreThanScreen: false,
        tableElement: null,
        nameGroupCheckbox: '',
        paginedData: [],
        isIndeterminateTotal: false,
        sortField: '',
        sortIncrease: false
      };
    },
    computed: {
      hasSlot() {
        return (name = 'default') => {
          return !!this.$slots[name] || !!this.$scopedSlots[name];
        };
      },
      isRowSelected() {
        return this.paginedData.some((item) => item.isChecked);
      },
      isCheckedTotal() {
        return this.paginedData.every((item) => item.isChecked);
      }
    },
    mounted() {
      this.nameGroupCheckbox = genRandomId();
      this.totalColWidth = this.columns.reduce((sum, col) => sum + col.width, 0);
      this.paginDataTable();
    },
    methods: {
      paginDataTable() {
        const isPaginedData = !!this.pagination && !!this.pagination.pageSize && !!this.pagination.current;
        this.paginedData = isPaginedData && this.clientPaging ? [...this.dataSource].splice(this.pagination.pageSize * (this.pagination.current - 1), this.pagination.pageSize) : [...this.dataSource];
      },
      setSizeTable(width, height) {
        this.height = height ? height + 'px' : this.height;
        const widthTableAsPx = width && !isNaN(width) ? width : 0;
        this.isTableWidthMoreThanScreen = this.totalColWidth && widthTableAsPx && this.totalColWidth >= widthTableAsPx;
        let widthTableInit = this.totalColWidth;
        if (this.hasSelectRow) {
          widthTableInit += 50;
        }
        const widthOfTable = Math.max(width, widthTableInit);
        this.width = widthOfTable && widthOfTable > 80 ? widthOfTable + 'px' : '100%';
      },
      onChangeCheckBoxTotal(event) {
        this.paginedData.forEach((item) => item.isChecked = event.checked);
        setTimeout(() => {
          this.$emit(
            outputChangeRowName,
            this.dataSource.filter((item) => item.isChecked).map((item) => item.id)
          );
        });
      },
      onChangeSingleCheckBox(record, indexRecord, event) {
        record['isChecked'] = event.checked;
        setTimeout(() => {
          this.isIndeterminateTotal = this.paginedData.some((item) => item.isChecked);
          this.$emit(
            outputChangeRowName,
            this.dataSource.filter((item) => item.isChecked).map((item) => item.id)
          );
        });
      },
      onClickHeader(event, sorter, sortField) {
        event.stopPropagation();
        if (sorter) {
          this.sortField = sortField;
          if (this.sortIncrease) {
            this.sortIncrease = false;
            this.dataSource.sort(sorter);
          } else {
            this.sortIncrease = true;
            this.dataSource.sort(sorter).reverse();
          }
          setTimeout(() => {
            this.paginDataTable();
          });
        }
      },
      onRowClick(record, index) {
        if (this.rowClickable) {
          this.$emit('row-click', record, index);
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .table-container {
    overflow-x: hidden;
    overflow-y: auto;
    transition: width 300ms linear, height 300ms linear;
  }
  table {
    width: 100%;
    margin: auto;
    table-layout: auto;
    background-color: #ffffff;
  }
  .fixed {
    table-layout: fixed;
  }
  .checkbox {
    width: 50px;
    min-width: 40px;
    max-width: 50px;
  }
  table,
  td,
  th {
    border-collapse: separate;
    border-spacing: 0;
  }
  th {
    padding: 10px;
    color: #323232;
    font-family: Roboto;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 20px;
    &.sort > div {
      cursor: pointer;
      display: flex;
      align-items: flex-start;
    }
  }
  th .sort-icon {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 14px;
    margin-left: 5px;
    & > span {
      height: 2px;
      width: 4px;
      background-color: currentColor;
      opacity: 0.4;
      transition: width 300ms linear;
      &:nth-child(1) {
        width: 10px;
      }
      &:nth-child(2) {
        width: 7px;
        margin: 2px 0px;
      }
      &:nth-child(3) {
        width: 4px;
      }
    }
    &.current > span {
      opacity: 1;
    }
    &.current.increase > span {
      &:nth-child(1) {
        width: 10px;
      }
      &:nth-child(3) {
        width: 4px;
      }
    }
    &.current.decrease > span {
      &:nth-child(1) {
        width: 4px;
      }
      &:nth-child(3) {
        width: 10px;
      }
    }
  }
  td {
    vertical-align: top;
    padding: 10px;
    color: #323232;
    font-family: Roboto;
    font-size: 13px;
    letter-spacing: 0;
    line-height: 20px;
  }
  th,
  td {
    text-align: left;
  }
  thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #ffffff;
    border-bottom: 1px solid #cecece;
  }
  tbody > tr {
    &:not(:last-child) {
      border-bottom: 1px solid #e9e9e9;
    }
    &:nth-child(even) {
      background-color: #ffffff;
    }
    &:nth-child(odd) {
      background-color: #f7f7f7;
    }
    &:hover {
      background-color: #e9f7f7;
    }
    &.clickable {
      cursor: pointer;
    }
  }
</style>
